import { useEffect, useState } from "react";
import { Banner } from "./Banner";
import { Button } from "./Button";
import { messageOf } from "../lib/errors";
import {
  disablePush,
  enablePush,
  pushSnoozed,
  pushState,
  snoozePush,
  type PushState,
} from "../lib/push";

const WHY_ON_BOARD =
  "Monday mornings: the new gigs in your skill. Friday afternoons: a one-minute check-in on how your week went.";
const WHY_BEFORE_BOARD =
  "If a day goes by without progress, we'll nudge you each evening until you reach the Gig Board. Then: new gigs every Monday, a check-in every Friday.";

type Outcome = { tone: "good" | "warn" | "bad"; text: string } | null;

/**
 * "Get a weekly nudge" (on the Gig Board) or "Stay on track." (before it): turns on reminders for this device, the
 * Monday and Friday rounds plus the evening nudge while not yet on the Gig Board. On the Gig Board it always shows
 * (and offers Turn off once on); on the hub it can be put off for 14 days with "Not now".
 */
export function PushCard({ onBoard }: { onBoard: boolean }) {
  const [state, setState] = useState<PushState | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => !onBoard && pushSnoozed());

  useEffect(() => {
    let cancelled = false;
    pushState()
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch(() => {
        if (!cancelled) setState("off");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (outcome) return <Banner tone={outcome.tone}>{outcome.text}</Banner>;
  if (hidden || !state) return null;

  const lead = onBoard ? "Get a weekly nudge." : "Stay on track.";
  const why = onBoard ? WHY_ON_BOARD : WHY_BEFORE_BOARD;

  const later = () => {
    snoozePush();
    setHidden(true);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const on = await enablePush();
      setOutcome(
        on
          ? { tone: "good", text: "✓ Reminders are on. See you on Monday." }
          : {
              tone: "warn",
              text: "Reminders stay off. You can turn them on later in your browser's site settings.",
            },
      );
    } catch (error) {
      setOutcome({
        tone: "bad",
        text: `We couldn't turn on reminders on this device. ${messageOf(error)}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
    } catch {
      /* Re-read the state below either way. */
    }
    setState(await pushState().catch(() => "off" as const));
    setBusy(false);
  };

  if (state === "ask") {
    return (
      <div className="my-3 flex flex-wrap items-center justify-between gap-2.5 rounded-[var(--radius-control)] border border-warning/40 bg-warning-tint px-4 py-3 text-[14px] leading-[1.5]">
        <div>
          <b>{lead}</b> {why}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => void turnOn()}>
            Turn on reminders
          </Button>
          {onBoard ? null : (
            <Button size="sm" variant="ghost" onClick={later}>
              Not now
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (state === "ios") {
    return (
      <Banner tone="warn">
        <b>{lead}</b> {why} On iPhone, add GATEWAY to your home screen first: tap Share, then{" "}
        <b>Add to Home Screen</b>, then open GATEWAY from your home screen and sign in.
        {onBoard ? null : (
          <>
            {" "}
            <button type="button" className="underline" onClick={later}>
              Not now
            </button>
          </>
        )}
      </Banner>
    );
  }

  if (state === "on" && onBoard) {
    return (
      <p className="text-[13px] text-text-muted">
        🔔 Weekly reminders are on for this device.{" "}
        <button
          type="button"
          className="text-accent underline"
          disabled={busy}
          onClick={() => void turnOff()}
        >
          Turn off
        </button>
      </p>
    );
  }

  return null;
}
